import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import LogInModal from 'flarum/forum/components/LogInModal';
import Lightbox from './Lightbox';
import TranslationNotice from './TranslationNotice';
import { formatDate } from '../utils/format';
import { t, tt, route, find, syncLocale, contentLang } from '../i18n';

const PREVIEW = { anecdotes: 8 };
const LONG_HISTORY = 4000;

/**
 * /figuren/:slug — één markante figuur: intro, verhaal, clubs, tijdlijn, herinneringen van het forum en discussies.
 */
export default class FigurePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.expanded = {};
    this.lightbox = null;
    syncLocale();
    this.load(m.route.param('slug'));
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const slug = m.route.param('slug');
    if (this.figure && slug !== this.figure.slug()) {
      this.expanded = {};
      this.load(slug);
    }
  }

  load(slug) {
    this.figure = app.store.all('lc-figures').find((f) => f.slug() === slug && f.bioHtml() !== undefined) || null;
    this.discussions = null;
    if (this.figure) this.ready();

    find('lc-figures', slug).then((figure) => {
      this.figure = figure;
      this.ready();
      m.redraw();
    });

    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  ready() {
    const figure = this.figure;
    app.history.push('lc.figure', figure.name());
    app.setTitle(figure.name());
    app.setTitleCount(0);

    const q = `"${(figure.searchNames() || [figure.name()])[0]}"`;
    if (!this.discussions || this.discussions.params.q !== q) {
      this.discussions = new DiscussionListState({ q, sort: 'relevance' });
      this.discussions.refresh();
    }
  }

  view() {
    const figure = this.figure;
    if (!figure) return <div className="LcClubPage"><LoadingIndicator /></div>;

    return (
      <div className="LcClubPage LcArtistPage LcFigurePage">
        {this.hero()}

        <div className="container LcClubBody">
          <main className="LcClubMain">
            {this.story()}
            {this.anecdotes()}
            {this.photos()}
            {this.clubList()}
            {this.timeline()}
            {this.forum()}
          </main>

          <aside className="LcClubSide">{this.sidebar()}</aside>
        </div>

        {this.lightbox ? (
          <Lightbox
            items={this.lightbox.items}
            index={this.lightbox.index}
            base=""
            onclose={() => {
              this.lightbox = null;
              m.redraw();
            }}
          />
        ) : null}
      </div>
    );
  }

  hero() {
    const figure = this.figure;
    const clubs = this.clubSlugs();
    const photos = figure.photos() || [];

    const facts = [
      { icon: 'fas fa-user', text: figure.role() || tt(`figure.type.${figure.type()}`) },
      figure.activeYears() && { icon: 'far fa-calendar', text: figure.activeYears() },
      clubs.length && { icon: 'fas fa-map-marker-alt', text: clubs.map((s) => this.clubName(s)).slice(0, 3).join(', ') },
    ].filter(Boolean);

    return (
      <section className="LcClubHero LcArtistHero">
        <div className="container LcClubHero-inner">
          <div className="LcClubHero-text">
            <Link className="LcBack" href={route('lc.figures')}>
              <i className="fas fa-arrow-left" aria-hidden="true" /> {t('figure.back')}
            </Link>
            <h1 className="LcClubHero-title">{figure.name()}</h1>
            {figure.shownAliases().length ? <p className="LcAliases">{t('common.aliases', { names: figure.shownAliases().join(' · ') })}</p> : null}
            <ul className="LcFacts">
              {facts.map((f) => (
                <li>
                  <i className={f.icon} aria-hidden="true" /> {f.text}
                </li>
              ))}
            </ul>
            {figure.description() ? <p className="LcClubHero-lead" lang={contentLang(figure)}>{figure.description()}</p> : null}
            <TranslationNotice model={figure} />
          </div>
          {figure.portrait() ? (
            <div className="LcArtistHero-portrait">
              {photos.length ? (
                <button className="LcFigurePortrait" onclick={() => (this.lightbox = { items: photos, index: 0 })} aria-label={tt('figure.view_photo')}>
                  <img src={figure.portrait()} alt={figure.name()} />
                </button>
              ) : (
                <img src={figure.portrait()} alt={figure.name()} />
              )}
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  /** Foto's uit de clubmedia waarop de figuur staat (volledige beelden, met bijschrift). */
  photos() {
    const items = this.figure.photos() || [];
    if (!items.length) return null;

    return (
      <section className="LcSection" id="fotos">
        <div className="LcSection-head">
          <h2>{t('club.photos')}</h2>
          <span className="LcSection-count">{items.length}</span>
        </div>
        <ul className="LcGallery LcGallery--photos">
          {items.map((item, i) => (
            <li>
              <button className="LcGallery-item" onclick={() => (this.lightbox = { items, index: i })}>
                <img src={item.thumb} alt={item.caption || ''} loading="lazy" />
                {item.date ? <span className="LcGallery-date">{formatDate(item.date)}</span> : null}
              </button>
              {item.caption ? <p className="LcGallery-caption">{item.caption}</p> : null}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  story() {
    const figure = this.figure;
    if (!figure.bioHtml()) return null;

    const long = figure.bioHtml().length > LONG_HISTORY;
    const open = !long || this.expanded.story;

    return (
      <section className="LcSection LcHistory" id="verhaal">
        <h2>{t('club.history')}</h2>
        <div className={'LcHistory-body' + (open ? '' : ' is-collapsed')} lang={contentLang(figure)}>
          {m.trust(figure.bioHtml())}
        </div>
        {long ? (
          <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-book-open'} onclick={() => (this.expanded.story = !open)}>
            {open ? t('club.read_less') : t('club.read_more')}
          </Button>
        ) : null}
      </section>
    );
  }

  /** De clubs van deze figuur, met rol en jaren, oudste club eerst. */
  clubList() {
    const groups = {};
    (this.figure.clubs() || []).forEach((c) => (groups[c.club] = groups[c.club] || []).push(c));
    const slugs = Object.keys(groups);
    if (!slugs.length) return null;

    const club = (s) => app.store.all('lc-clubs').find((c) => c.slug() === s);
    slugs.sort((a, b) => ((club(a) && club(a).opened()) || 9999) - ((club(b) && club(b).opened()) || 9999));

    return (
      <section className="LcSection" id="clubs">
        <div className="LcSection-head">
          <h2>{t('figure.clubs')}</h2>
          <span className="LcSection-count">{slugs.length}</span>
        </div>
        <ul className="LcArtistClubs">
          {slugs.map((s) => {
            const c = club(s);
            return (
              <li>
                {c ? (
                  <Link href={route('lc.club', { slug: s })} className="LcArtistClubs-club">
                    <strong>{c.name()}</strong>
                    <span>{[c.city(), c.years()].filter(Boolean).join(' · ')}</span>
                  </Link>
                ) : (
                  <span className="LcArtistClubs-club">
                    <strong>{s}</strong>
                  </span>
                )}
                <ul className="LcArtistClubs-roles">
                  {groups[s].map((r) => (r.role || r.years ? <li>{[r.role, r.years].filter(Boolean).join(' · ')}</li> : null))}
                </ul>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  timeline() {
    const items = this.figure.timeline() || [];
    if (!items.length) return null;

    return (
      <section className="LcSection" id="tijdlijn">
        <h2>{t('club.timeline')}</h2>
        <ol className="LcTimeline" lang={contentLang(this.figure)}>
          {items.map((i) => (
            <li>
              <span className="LcTimeline-year">{i.year}</span>
              <p>{i.text}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  /** Herinneringen uit het forum, elk met een link naar het bericht. */
  anecdotes() {
    const items = this.figure.anecdotes() || [];
    if (!items.length) return null;
    const shown = this.expanded.anecdotes ? items : items.slice(0, PREVIEW.anecdotes);
    const open = this.expanded.anecdotes;

    return (
      <section className="LcSection" id="anekdotes">
        <div className="LcSection-head">
          <h2>{t('artist.anecdotes')}</h2>
          <span className="LcSection-count">{items.length}</span>
        </div>
        <p className="LcNote">{t('figure.anecdotes_note')}</p>
        <div className="LcAnecdotes">
          {shown.map((a) => (
            <figure className="LcAnecdote">
              <blockquote lang="nl">{a.text}</blockquote>
              <figcaption>
                {a.context ? <span>{a.context}</span> : null}
                {a.date ? <span>{formatDate(a.date)}</span> : null}
                {a.discussion_id ? (
                  <a href={app.forum.attribute('baseUrl') + `/d/${a.discussion_id}/${a.number || 1}`}>
                    {t('artist.read_post')} <i className="fas fa-arrow-right" aria-hidden="true" />
                  </a>
                ) : null}
              </figcaption>
            </figure>
          ))}
        </div>
        {items.length > PREVIEW.anecdotes ? (
          <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-th'} onclick={() => (this.expanded.anecdotes = !open)}>
            {open ? t('club.show_less') : t('club.show_all', { count: items.length })}
          </Button>
        ) : null}
      </section>
    );
  }

  forum() {
    return (
      <section className="LcSection LcDiscussions" id="forum">
        <div className="LcDiscussions-head">
          <h2>{t('artist.discussions', { name: this.figure.name() })}</h2>
          <Button className="Button Button--primary" icon="fas fa-pen" onclick={() => this.compose()}>
            {t('club.start_discussion')}
          </Button>
        </div>
        {this.discussions ? <DiscussionList state={this.discussions} /> : null}
      </section>
    );
  }

  sidebar() {
    const figure = this.figure;
    const facts = [
      [t('figure.fact.type'), t(`figure.type.${figure.type()}`)],
      [t('figure.fact.role'), figure.role()],
      [t('artist.fact.active'), figure.activeYears()],
    ].filter(([, v]) => v);

    return [
      <section className="LcPanel LcPanel--facts">
        <h3>{t('club.facts')}</h3>
        <dl className="LcFactList">{facts.map(([k, v]) => [<dt>{k}</dt>, <dd>{v}</dd>])}</dl>
      </section>,

      (figure.sources() || []).length ? (
        <section className="LcPanel">
          <h3>{t('club.sources')}</h3>
          <ul className="LcSources">
            {figure.sources().map((s) => (
              <li>
                <a href={s.url} target="_blank" rel="noopener nofollow ugc">
                  {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      <p className="LcNote">{figure.isVerified() ? t('artist.correction') : t('artist.unverified')}</p>,
    ];
  }

  clubSlugs() {
    return Array.from(new Set((this.figure.clubs() || []).map((c) => c.club)));
  }

  clubName(slug) {
    const club = app.store.all('lc-clubs').find((c) => c.slug() === slug);
    return club ? club.name() : slug;
  }

  compose() {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    // Nieuwe discussies over een figuur komen onder de tag van zijn eerste club.
    const first = this.clubSlugs().map((s) => app.store.all('lc-clubs').find((c) => c.slug() === s)).find((c) => c && c.tag && c.tag());
    const tag = first ? first.tag() : null;
    const tags = tag ? [tag.parent && tag.parent(), tag].filter(Boolean) : [];
    app.composer.load(DiscussionComposer, { user: app.session.user, tags });
    app.composer.show();
  }
}
